import styled from 'styled-components';
import { alpha } from '../../../themes/brand';

export const Container = styled.div`
  padding: 24px;
  background-color: ${({theme}) => theme.background};
  height: 100%;
  overflow-x: hidden;
  max-width: 100%;
  box-sizing: border-box;
`;

export const Header = styled.div`
  position: fixed;
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  z-index: 10;
`;

export const BackButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  
  .icon {
    color: ${({ theme }) => theme.arrowBack};
  }

  svg {
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: translateX(-2px);
  }
`;

export const Title = styled.h1`
  color: ${props => props.theme.text};
  margin: 0;
  font-size: 32px;
  font-weight: 700;
`;

export const Subtitle = styled.p`
  color: ${props => props.theme.textSubtitleCompliance};
  margin: 8px 0 32px 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
`;

export const PendingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  margin: 20px 0;
  background-color: ${({theme}) => theme.formUserInput || theme.placeholderColor};
  border-radius: 8px;
  text-align: center;
`;

export const PendingMessage = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: ${({theme}) => theme.text};
  margin: 16px 0;
`;

export const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: ${({theme}) => theme.background};
  color: ${({theme}) => theme.text};
  margin-bottom: 16px;
  
  svg {
    animation: spin 2s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const PendingText = styled.p`
  font-size: 16px;
  line-height: 1.5;
  color: ${({theme}) => theme.textSubtitleCompliance};
  max-width: 600px;
  margin: 0;
`;

export const FormTitle = styled.h2`
  color: ${props => props.theme.text};
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 600;
`;

export const FormDescription = styled.p`
  color: ${props => props.theme.text};
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
`;

export const RequiredFieldsNote = styled.p`
  color: ${props => props.theme.text};
  margin: 0 0 16px 0;
  font-size: 14px;
  font-weight: 400;
  opacity: 0.8;
  
  &::before {
    content: "* ";
    color: ${({ theme }) => theme.primaryAccent};
    margin-right: 4px;
  }
`;

export const FormContainer = styled.div`
  width: 100%;
  padding: 0 40px;
  box-sizing: border-box;
  max-width: 1200px;
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 100%;
`;

export const FormGroup = styled.div`
  margin-bottom: 20px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const Label = styled.label`
  flex: 0 0 35%;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
  color: ${props => props.theme.text};
  
  @media (max-width: 768px) {
    margin-bottom: 8px;
    width: 100%;
  }
`;

export const Input = styled.input`
  flex: 1 1 auto;
  min-width: 300px;
  height: 48px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid ${({theme}) => theme.borderInputCompliance};
  background-color: ${({theme}) => theme.formUserInput || theme.placeholderColor};
  color: ${({theme}) => theme.text};
  font-size: 16px;
  box-sizing: border-box;
  margin-left: auto;
  
  &:focus {
    outline: none;
    border: 1px solid ${({ theme }) => theme.primaryAccent};
  }
  
  &:disabled {
    background-color: ${({theme}) => theme.disabledBackground || theme.background};
    cursor: not-allowed;
  }
`;

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
  padding-right: 5px;
`;

export const SubmitButton = styled.button`
  background-color: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.primaryOn};
  border: none;
  border-radius: 30px;
  padding: 10px 28px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${({ theme }) => theme.primary};
  }
  
  &:disabled {
    background-color: ${({ theme }) => theme.primary};
    cursor: not-allowed;
  }
`;

export const TimelineContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  width: 100%;
  position: relative;
`;

export const TimelineProgress = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  height: 4px;
  background-color: ${({ theme }) => theme.primary};
  transform: translateY(-50%);
  z-index: 1;
  transition: width 0.3s ease;
`;

export const TimelineBackground = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  height: 4px;
  width: 100%;
  background-color: ${({theme}) => theme.borderInputCompliance};
  transform: translateY(-50%);
`;

export const StepItem = styled.div<{ active: boolean; completed: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
`;

export const StepCircle = styled.div<{ active: boolean; completed: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  margin-bottom: 8px;
  background-color: ${({ active, completed, theme }) => 
    completed ? theme.primary : active ? theme.card : theme.formUserInput};
  color: ${({ active, completed, theme }) => 
    completed ? theme.primaryOn : active ? theme.primaryAccent : theme.text};
  border: 2px solid ${({ theme, active, completed }) => 
    completed || active ? theme.primaryAccent : 'transparent'};
  transition: all 0.3s ease;
`;

export const StepLabel = styled.span<{ active: boolean }>`
  font-size: 12px;
  color: ${({ active, theme }) => active ? theme.primaryAccent : theme.text};
  font-weight: ${({ active }) => active ? '600' : '400'};
`;

export const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 32px;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

export const SectionTitle = styled.h3`
  color: ${props => props.theme.text};
  margin: 0;
  font-size: 20px;
  font-weight: 600;
`;

export const SectionDescription = styled.p`
  color: ${props => props.theme.textSubtitleCompliance};
  margin: 8px 0 0 0;
  font-size: 14px;
  font-weight: 400;
`;

export const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 32px;
`;

export const PrevButton = styled.button`
  background-color: transparent;
  color: ${({theme}) => theme.text};
  border: 1px solid ${({theme}) => theme.borderInputCompliance};
  border-radius: 30px;
  padding: 10px 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background-color: ${({ theme }) => alpha(theme.primary, 0.1)};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const NextButton = styled.button`
  background-color: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.primaryOn};
  border: none;
  border-radius: 30px;
  padding: 10px 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background-color: ${({ theme }) => theme.primary};
  }
  
  &:disabled {
    background-color: ${({ theme }) => theme.primary};
    cursor: not-allowed;
  }
`;

export const SectionStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 16px;
`;

export const StatusText = styled.span`
  font-size: 14px;
  color: ${({theme}) => theme.textSubtitleCompliance};
`;

export const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: ${({theme}) => theme.borderInputCompliance};
  border-radius: 4px;
  margin-top: 8px;
  overflow: hidden;
`;

export const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background-color: ${({ theme }) => theme.primary};
  width: ${({ progress }) => `${progress}%`};
  transition: width 0.3s ease;
  border-radius: 4px;
`;

export const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  margin-right: 8px;
  cursor: pointer;
`;

export const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  color: ${props => props.theme.text};
  font-size: 16px;
  font-weight: 500;
`;

export const CheckboxContainer = styled.div`
  display: flex;
  flex: 0 0 60%;
  align-items: center;
`;

export const ConditionalField = styled.div<{ visible: boolean }>`
  margin-top: ${({ visible }) => visible ? '16px' : '0'};
  margin-bottom: ${({ visible }) => visible ? '16px' : '0'};
  height: ${({ visible }) => visible ? 'auto' : '0'};
  opacity: ${({ visible }) => visible ? '1' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
  width: 100%;
  
  ${FormGroup} {
    margin-left: 0;
  }
  
  ${Input} {
    min-width: 300px;
    margin-left: auto;
  }
`;

export const CheckboxGroup = styled.div`
  display: flex;
  flex: 0 0 60%;
  gap: 24px;
`;

export const CheckboxOption = styled.div`
  display: flex;
  align-items: center;
`;

export const RadioGroup = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1 1 auto;
  min-width: 300px;
  margin-left: auto;
`;

export const RadioOption = styled.div`
  display: flex;
  align-items: center;
`;

export const Radio = styled.input`
  width: 18px;
  height: 18px;
  margin-right: 8px;
  cursor: pointer;
`;

export const RadioLabel = styled.label`
  color: ${props => props.theme.text};
  font-size: 16px;
  cursor: pointer;
`;

export const CheckboxGroupHorizontal = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
  flex: 1 1 auto;
  min-width: 300px;
  margin-left: auto;
`;

export const CheckboxWithLabel = styled.div`
  display: flex;
  align-items: center;
  margin-right: 24px;
`;

export const Toast = styled.div<{ type: 'success' | 'error' }>`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: ${props => props.type === 'success' 
    ? props.theme.success 
    : props.theme.backgroundConfirmButtonDeleteModal};
  color: ${({ theme }) => theme.errorOn};
  padding: 12px 24px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 1100;
  animation: fadeIn 0.3s ease-out forwards, fadeOut 0.3s ease-out 2.7s forwards;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes fadeOut {
    from { opacity: 1; transform: translateY(0); }
    to { opacity: 0; transform: translateY(20px); }
  }
`; 